import {
  createIntegrationConnection,
  disconnectIntegrationConnection,
  type IntegrationConnection
} from '../../common/utils/integrations-client/jira/connections';
import { SupportedIntegrationProductSlugs } from '../../common/constants';
import { initiateConnection } from '../../common/utils/integrations-client/jira/on-prem/connection';
import { getDeleteConnection } from './getDeleteConnection';
import { type IntegrationProductInfo } from '../../common/utils/get-integration-product-name-from-slug';
import { type AuthUser } from '../../common/contexts/auth';
import { type v2 } from '@deque/billing-service-client';
import type AxeAccountAnalytics from '../../common/AxeAccountAnalytics';
import type AxeDevtoolsWatcherAnalytics from '../../axe-watcher/AxeDevtoolsWatcherAnalytics';
import type AxeDevtoolsProAnalytics from '../../axe-devtools/AxeDevtoolsProAnalytics';
import type CrossProductAnalytics from '../../common/CrossProductAnalytics';
import type AxeDevtoolsMobileAnalytics from '../../axe-devtools-mobile/AxeDevToolsMobileAnalytics';
import type Analytics from '../../axe-spider/Analytics';
import { type SetContentsFunction } from '../../common/contexts/globalToast';
import { type TFunction } from 'react-i18next';

export type GetActionsFnParams = {
  displayName: string;
  integrationProductInfo: IntegrationProductInfo;
  connection: IntegrationConnection | null;
  authUser: AuthUser;
  enterprise: v2.Enterprise;
  analytics:
    | AxeAccountAnalytics
    | AxeDevtoolsWatcherAnalytics
    | AxeDevtoolsProAnalytics
    | CrossProductAnalytics<string>
    | AxeDevtoolsMobileAnalytics
    | Analytics;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  connectionsUpdated: () => void;
  setContents: SetContentsFunction;
  t: TFunction<'translation', undefined>;
};

export type getActionsFn = ({
  displayName,
  integrationProductInfo,
  connection,
  authUser,
  enterprise,
  analytics,
  setIsProcessing,
  setError,
  connectionsUpdated,
  setContents,
  t
}: GetActionsFnParams) => {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  deleteConnection: () => Promise<void>;
};

export const getJiraOnPremActions: getActionsFn = ({
  displayName,
  integrationProductInfo,
  connection,
  authUser,
  enterprise,
  analytics,
  setIsProcessing,
  setError,
  connectionsUpdated,
  setContents,
  t
}) => {
  const connect = async (): Promise<void> => {
    try {
      setIsProcessing(true);

      if (connection) {
        const initConnection = await initiateConnection({
          enterpriseId: enterprise.id,
          token: authUser.token,
          connectionId: connection.id
        });
        window.open(
          initConnection.redirect_url,
          '_blank',
          'popup,width=650,height=650'
        );

        // Notification, analytics and connections refesh will happen when connection is done
        // in JiraOnPremIntegrationConfigurations.tsx (when corresponding message is received)
      } else {
        await createIntegrationConnection({
          enterpriseId: enterprise.id,
          token: authUser.token,
          body: {
            product_slug:
              SupportedIntegrationProductSlugs.jiraOnPremIntegration,
            status: 'disconnected'
          }
        });

        connectionsUpdated();
      }
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      setError(
        t(
          'There was an error connecting to {{ displayName }}. Please try again.',
          {
            displayName
          }
        )
      );

      await analytics.integrationConnectFail({
        integration:
          integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      if (connection) {
        setIsProcessing(true);

        // Update server side connection
        await disconnectIntegrationConnection({
          enterpriseId: enterprise.id,
          token: authUser.token,
          connectionId: connection.id
        });

        setContents(
          t('Successfully disconnected from {{integrationType}}', {
            integrationType: displayName
          }),
          'confirmation'
        );

        await analytics.integrationDisconnect({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });

        connectionsUpdated();
      }
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      setError(
        t(
          'There was an error disconnecting from {{ displayName }}. Please try again.',
          {
            displayName
          }
        )
      );

      await analytics.integrationDisconnectFail({
        integration:
          integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteConnection = getDeleteConnection({
    displayName,
    connection,
    authUser,
    enterprise,
    setIsProcessing,
    setError,
    connectionsUpdated,
    setContents,
    t
  }).deleteConnection;

  return {
    connect,
    disconnect,
    deleteConnection
  };
};
